import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import { useCurrentUser } from '../../contexts/CurrentUserProvider';
import { IdsButton, IdsSectionNotice } from '@investec/ids-web-react';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import IsaTransferOtpModal from './IsaTransferOtpModal';

import './isaTransferProviderSummary.scss';

const SEND_OTP = gql`
    mutation SendLoggedInOtp($request: SendOtpRequestType!) {
        sendLoggedInOtp(request: $request) { success verificationToken }
    }
`;

interface IsaTransferProviderSummaryStepProps {
    accountId: string;
    accountNumber: string;
    sortCode: string;
    productName: string;
    providerDetails: {
        provider: {
            id: string;
            name: string;
            serviceUserNumber: string;
        };
        sortCode?: string;
        accountNumber: string;
        rollNumber?: string;
    };
    amountDetails: {
        transferType: 'full' | 'partial';
        totalTransferAmount: number | null;
        includesCurrentTaxYear: boolean;
        currentTaxYearAmount: number | null;
    };
    onBack: () => void;
    onEdit?: () => void;
    onComplete: () => void;
}

interface IsaTransferPayload {
    verifyOtp: { verificationToken: string; passCode: string; mode: string };
    providerId: string;
    providerName: string;
    providerServiceUserNumber: string;
    rollNumber: string;
    sortCode: string;
    accountNumber: string;
    transferType: 'full' | 'partial';
    totalTransferAmount: number | null;
    includesCurrentTaxYear: boolean;
    currentTaxYearAmount: number | null;
    destinationAccountId: string;
    destinationAccountNumber: string;
    destinationSortCode: string;
    destinationProductName: string;
    acceptEarlyWaiverFee: boolean;
}

const IsaTransferProviderSummaryStep: React.FC<IsaTransferProviderSummaryStepProps> = ({
    accountId,
    accountNumber: destAccountNumber,
    sortCode: destSortCode,
    productName: destProductName,
    providerDetails,
    amountDetails,
    onBack,
    onEdit = onBack,
    onComplete
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [otpError, setOtpError] = useState<string | null>(null);
    const [otpLoading, setOtpLoading] = useState(false);
    const [understandsFee, setUnderstandsFee] = useState(false);
    const [verificationToken, setVerificationToken] = useState('');
    const [sendOtp] = useMutation(SEND_OTP);
    const currentUser = useCurrentUser();
    const analytics = useUnifiedAnalytics();

    useEffect(() => {
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                'summary view',
                {},
                'ISA Transfer Summary',
                'ISA_Transfer_Summary|Page_Load|Summary_View'
            );
        }
    }, [analytics]);

    const handleBack = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'back',
                '',
                'isa transfer summary',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Summary|Button_Click|Back',
                'ISA Transfer Summary'
            );
        }
        onBack();
    };

    const handleEdit = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'edit',
                '',
                'isa transfer summary',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Summary|Link_Click|Edit',
                'ISA Transfer Summary'
            );
        }
        onEdit();
    };

    const formatCurrency = (value: number | null): string => {
        if (value === null) return 'N/A';
        return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatSortCode = (sc: string): string => {
        const d = sc.replace(/\D/g, '');
        if (d.length <= 2) return d;
        if (d.length <= 4) return `${d.slice(0, 2)}-${d.slice(2)}`;
        return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
    };

    const handleStartTransfer = async () => {
        if (isSubmitting) return;
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'continue',
                '',
                'isa transfer summary',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Summary|Button_Click|Continue',
                'ISA Transfer Summary'
            );
        }

        setIsSubmitting(true);
        setVerificationToken('');
        setShowOtp(true);
        setOtpError(null);

        try {
            const result = await sendOtp({ variables: { request: {} } });
            const token = result?.data?.sendLoggedInOtp?.verificationToken;

            // Match the Investec transfer flow: the token starts the OTP journey.
            // The transfer API still verifies the entered code before submission.
            if (!token)
                throw new Error('Failed to send OTP');

            setVerificationToken(token);
        } catch {
            setOtpError('Failed to send OTP. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpClose = () => {
        if (analytics) {
            analytics.trackAnalyticsOtpEvent('close', 'popup', 'close', 'digital savings', 'ISA_Transfer_Summary|OTP|Close');
        }
        setShowOtp(false);
        setOtpError(null);
    };

    const handleOtpContinue = async (otp: string, otpMode: string) => {
        if (otpLoading) return false;
        setOtpLoading(true);
        setOtpError(null);

        try {
            if (!currentUser?.accessToken) throw new Error('Please sign in again to continue.');
            if (!verificationToken) throw new Error('Please request a new code.');

            if (analytics) {
                analytics.trackAnalyticsOtpEvent('continue', 'popup', 'continue', 'digital savings', 'ISA_Transfer_Summary|OTP|Continue');
                analytics.trackAnalyticsClickTransactionEvent(
                    'transfer submitted',
                    'ISA Transfer',
                    'isa transfer',
                    'Instant Access Cash ISA',
                    'ISA_Transfer_Summary|Transaction|Transfer_Submitted',
                    'ISA Transfer Summary',
                    ''
                );
            }

            const payload: IsaTransferPayload = {
                verifyOtp: { verificationToken, passCode: otp, mode: otpMode },
                providerId: providerDetails.provider.id,
                providerName: providerDetails.provider.name,
                providerServiceUserNumber: providerDetails.provider.serviceUserNumber,
                rollNumber: providerDetails.rollNumber ?? '',
                sortCode: (providerDetails.sortCode ?? '').replace(/\D/g, ''),
                accountNumber: providerDetails.accountNumber.replace(/\D/g, ''),
                transferType: amountDetails.transferType,
                totalTransferAmount: amountDetails.totalTransferAmount,
                includesCurrentTaxYear: amountDetails.includesCurrentTaxYear,
                currentTaxYearAmount: amountDetails.currentTaxYearAmount,
                destinationAccountId: accountId,
                destinationAccountNumber: destAccountNumber,
                destinationSortCode: destSortCode,
                destinationProductName: destProductName,
                acceptEarlyWaiverFee: true
            };

            const transferResponse = await fetch('/api/isa-transfer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${currentUser.accessToken}`
                },
                body: JSON.stringify(payload)
            });

            if (!transferResponse.ok) {
                const errData = await transferResponse.json().catch(() => null);
                throw new Error(errData?.message || 'Failed to submit ISA transfer. Please try again.');
            }

            setShowOtp(false);
            onComplete();
            return true;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'An unexpected error occurred';
            setOtpError(message);
            return false;
        } finally {
            setOtpLoading(false);
        }
    };

    return (
        <main className="isa-transfer-provider-summary">
            <p className="isa-transfer-provider-summary__title">Summary</p>

            <p className="isa-transfer-provider-summary__heading">
                You&apos;ve chosen to transfer your cash ISA
            </p>

            <p className="isa-transfer-provider-summary__description">
                Please check the details you&apos;ve provided are correct.
            </p>

            <div className="isa-transfer-provider-summary__card">
                <p className="isa-transfer-provider-summary__card-provider">
                    {providerDetails.provider.name}
                </p>

                <div className="isa-transfer-provider-summary__field">
                    <span className="isa-transfer-provider-summary__field-label">
                        Roll number or reference (if applicable)
                    </span>
                    <span className="isa-transfer-provider-summary__field-value">
                        {providerDetails.rollNumber || '–'}
                    </span>
                </div>

                {(providerDetails.sortCode || providerDetails.accountNumber) && (
                    <div className="isa-transfer-provider-summary__row">
                        {providerDetails.sortCode && (
                            <div className="isa-transfer-provider-summary__field">
                                <span className="isa-transfer-provider-summary__field-label">Sort code</span>
                                <span className="isa-transfer-provider-summary__field-value">
                                    {formatSortCode(providerDetails.sortCode)}
                                </span>
                            </div>
                        )}
                        {providerDetails.accountNumber && (
                            <div className="isa-transfer-provider-summary__field">
                                <span className="isa-transfer-provider-summary__field-label">Account number</span>
                                <span className="isa-transfer-provider-summary__field-value">
                                    {providerDetails.accountNumber}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div className="isa-transfer-provider-summary__row">
                    <div className="isa-transfer-provider-summary__field">
                        <span className="isa-transfer-provider-summary__field-label">Type of transfer</span>
                        <span className="isa-transfer-provider-summary__field-value">
                            {amountDetails.transferType === 'full' ? 'Full amount' : 'Partial amount'}
                        </span>
                    </div>
                    {amountDetails.transferType === 'partial' && (
                        <div className="isa-transfer-provider-summary__field">
                            <span className="isa-transfer-provider-summary__field-label">Total transfer amount</span>
                            <span className="isa-transfer-provider-summary__field-value">
                                {formatCurrency(amountDetails.totalTransferAmount)}
                            </span>
                        </div>
                    )}
                </div>

                <div className="isa-transfer-provider-summary__divider" />

                <div className="isa-transfer-provider-summary__tax-year-section">
                    <div className="isa-transfer-provider-summary__field">
                        <span className="isa-transfer-provider-summary__field-label">
                            {amountDetails.transferType === 'partial'
                                ? "Does the total amount you're transferring to us include any ISA contributions you've already made this tax year?"
                                : 'Does this include money contributed in the current tax year?'}
                        </span>
                        <span className="isa-transfer-provider-summary__field-value isa-transfer-provider-summary__field-value--small">
                            {amountDetails.includesCurrentTaxYear ? 'Yes' : 'No'}
                        </span>
                    </div>

                    {amountDetails.transferType === 'partial' && amountDetails.includesCurrentTaxYear && (
                        <div className="isa-transfer-provider-summary__field">
                            <span className="isa-transfer-provider-summary__field-label">
                                Amount you've paid into your ISA during this tax year.
                            </span>
                            <span className="isa-transfer-provider-summary__field-value isa-transfer-provider-summary__field-value--small">
                                {formatCurrency(amountDetails.currentTaxYearAmount)}
                            </span>
                        </div>
                    )}
                </div>

                <div className="isa-transfer-provider-summary__edit-row">
                    <button
                        type="button"
                        className="isa-transfer-provider-summary__edit-button"
                        onClick={handleEdit}
                    >
                        <span className="isa-transfer-provider-summary__edit-icon">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M11.3333 2L14 4.66667M2 12.6667V14H3.33333L12.3333 5L11 3.66667L2 12.6667Z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </span>
                        Edit
                    </button>
                </div>
            </div>

            <IdsSectionNotice
                appearance="information"
                className="isa-transfer-provider-summary__notice"
            >
                Your current ISA provider may charge a fee for this transfer. For example if you are transferring a fixed rate cash ISA before the maturity date.
                <br /><br />
                If you&apos;re unsure, please check with your current provider.
            </IdsSectionNotice>

            <label className="isa-transfer-provider-summary__checkbox">
                <input
                    type="checkbox"
                    checked={understandsFee}
                    onChange={(e) => setUnderstandsFee(e.target.checked)}
                />
                <span className="isa-transfer-provider-summary__checkbox-indicator" />
                <span className="isa-transfer-provider-summary__checkbox-label">
                    I understand that my current provider may charge a fee or limit transfers.
                </span>
            </label>

            <div className="isa-transfer-provider-summary__actions">
                <IdsButton appearance="default" size="md" disabled={isSubmitting} onClick={handleBack}>
                    Back
                </IdsButton>
                <IdsButton
                    appearance="primary"
                    size="md"
                    disabled={!understandsFee || isSubmitting}
                    onClick={handleStartTransfer}
                >
                    Continue
                </IdsButton>
            </div>

            <IsaTransferOtpModal
                isOpen={showOtp}
                isLoading={otpLoading}
                isSendingOtp={isSubmitting}
                onRetrySend={handleStartTransfer}
                errorMessage={otpError}
                verificationToken={verificationToken}
                onClose={handleOtpClose}
                onContinue={handleOtpContinue}
            />
        </main>
    );
};

export default IsaTransferProviderSummaryStep;
